/**
 * Telefon bildirimleri (Web Push). Kullanıcı "Bildirimleri aç"a bastığında tarayıcı izin ister; izin verilirse
 * bu cihaz sunucuya kaydedilir. Veresiye hatırlatmaları ve diğer bildirimler uygulama kapalıyken de gelir.
 *  - Android (Chrome): her durumda çalışır.
 *  - iPhone/iPad: yalnızca uygulama ana ekrana eklenmişse (iOS 16.4 ve üzeri).
 */
import * as ep from '../api/endpoints';

export type PushState = 'unsupported' | 'needs-install' | 'denied' | 'off' | 'on';

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export async function pushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub && Notification.permission === 'granted' ? 'on' : 'off';
}

function keyBytes(b64url: string): ArrayBuffer {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const raw = atob((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out.buffer;
}

/** İzin ister ve cihazı kaydeder. Başarılıysa true. */
export async function enablePush(): Promise<boolean> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;
  const reg = await navigator.serviceWorker.ready;
  const { public_key } = await ep.pushKey();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(public_key) });
  }
  await ep.pushSubscribe(sub.toJSON());
  return true;
}

export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  try { await ep.pushUnsubscribe(sub.endpoint); } finally { await sub.unsubscribe(); }
}

/** Giriş yapmış kullanıcının cihazı zaten izinliyse aboneliği sunucuyla eşitler (ör. hesap değişince). */
export async function syncPush(): Promise<void> {
  try {
    if ((await pushState()) !== 'on') return;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) await ep.pushSubscribe(sub.toJSON());
  } catch { /* bildirim isteğe bağlı; hata uygulamayı etkilemez */ }
}

/**
 * Çıkışta bu cihaz hesaptan ayrılır: telefonu başkası kullanırsa önceki kullanıcının bildirimleri gelmez.
 * Tarayıcı izni korunur; yeniden giriş yapılınca syncPush cihazı o hesaba bağlar.
 */
export async function detachPush(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const sub = await reg?.pushManager?.getSubscription();
    if (sub) await ep.pushUnsubscribe(sub.endpoint);
  } catch { /* bildirim isteğe bağlı */ }
}
